/**
 * The card shader — per-pixel lighting for a normal-mapped card quad.
 *
 * This is the argument for doing the game in 2D. There is no depth buffer and
 * no real light; instead every card carries a generated normal map and is lit
 * per-pixel against the single virtual `LIGHT` in `@/pixi/layout`:
 *
 * - a **warm key** with distance attenuation and a specular lobe,
 * - a **cool fill** from the opposite side so shadowed edges never go dead black,
 * - `LIGHT.ambient` underneath everything.
 *
 * The light vector is rebuilt **per fragment from that fragment's position on
 * the table**, not per sprite and not per frame — so a card sitting in the
 * left-hand pile is lit from a measurably different angle, and at a measurably
 * different brightness, than one on the right. That positional variation is what
 * makes the table read as lit rather than tinted.
 *
 * The vertex stage does the other half of the lie. A card's `lift`, its turn
 * about its own vertical axis and its travel tilt are collapsed into a single
 * view-space depth, and one perspective divide turns the quad into a real
 * trapezoid. `gl_Position.w` is set from that same divide, so the texture
 * interpolates perspective-correctly across it: a flipping card is a rotating
 * object, not a squashed rectangle.
 *
 * ## What the quad carries beyond its print
 *
 * The quad is deliberately **larger than the card** by `CARD_SHADING.margin`,
 * because three of the effects below have to paint *outside* the printed
 * silhouette. `vUV` is authored so that `0..1` still lands exactly on the print
 * and the margin runs negative / past one; a fragment out there is thrown away
 * on the first branch unless something has asked for it.
 *
 * - **Inter-card occlusion** (`uOccluder`). One rounded box, in this card's own
 *   frame, standing for whichever card is resting or hovering above it. The
 *   crevice darkening falls off over `uOccluderBox.z`, which the CPU grows with
 *   the gap between the two cards — so a card lying on another draws a hard
 *   black line and a card flying over it draws a soft grey pool. Same LIGHT,
 *   same offset law as the drop shadow, so the two can never disagree.
 * - **Motion blur** (`uBlur`). Taps along the card's own per-frame travel,
 *   inside this pass rather than as a screen-space smear, so a card that moves
 *   blurs and the table behind it does not. Zero strength takes the single-tap
 *   branch and is exactly the old image.
 * - **Edge thickness** (`uEdge`). A lit sliver of card stock along the side
 *   facing away from the light, widening with `uLift`. It is the same direction
 *   the drop shadow is thrown in, so the thickness and the shadow agree about
 *   where the card's far side is.
 * - **Travel sheen** (`uSheen`). An anisotropic specular lobe stretched along
 *   the card's velocity, driven by travel, spin and turn rate. Every card
 *   catches the key light as it moves, not just the two wilds.
 *
 * WebGL only, by design — the brief calls for real GLSL, so no WGSL twin is
 * provided. The application must init the renderer with `preference: 'webgl'`
 * (Pixi's default).
 */

import { GlProgram, MeshGeometry, Shader, UniformGroup } from 'pixi.js';

import { palette } from '@/core/tokens';
import { CARD, DESIGN, LIGHT, PILES } from '@/pixi/layout';
import { getFaceAtlasSource, getNormalAtlasSource, type CardUV } from '@/pixi/cardTexture';

/* ------------------------------------------------------------------ *
 * Shading tuning — the shader's own constants, single-sourced here.
 * ------------------------------------------------------------------ */

export const CARD_SHADING = {
  /**
   * Specular exponent.
   *
   * Raised from 42, which was far too wide to be a highlight at all. The
   * Blinn-Phong half-intensity half-angle for n=42 is 10.4 degrees, i.e. a
   * 20.8-degree lobe — but the half-vector only rotates about **5 degrees**
   * across an entire card face at this table's geometry. A lobe four times
   * wider than its subject cannot have a *position*: the specular term was
   * essentially constant over the whole card, so cards had a uniform gloss tint
   * rather than a glint that slides as they move. Worse, the baked paper tooth
   * jitters the normal by a comparable amount, so all of the visible specular
   * variation was per-texel sparkle and none of it was a coherent highlight.
   *
   * At 300 the half-angle is 3.4 degrees — narrower than the card's own sweep,
   * which is the condition for a highlight that travels.
   */
  shininess: 300,
  /** Specular strength for a card lying on the felt. */
  specBase: 0.55,
  /** Extra specular at `liftReference` — lift brightens the highlight. */
  specLift: 1.4,
  /** Lift (design units) treated as "fully in the air". */
  liftReference: 90,
  /** Distance scale of the key light's inverse-square falloff. */
  attenuationScale: 1400,
  /** Virtual camera height above the table, in design units. */
  viewDistance: 2400,
  /** How much the camera's angle changes across the table. 0 = orthographic. */
  viewParallax: 0.5,
  /** Strength of the sheen band dragged across the face during a turn. */
  sheen: 0.5,
  /** Distance from the table plane to the projection centre for the flip. */
  perspective: 1500,

  /**
   * How far the quad overhangs the printed card, in design units.
   *
   * The thickness sliver and the tail of a motion smear both live out here.
   * Fragments in the margin are discarded on the first branch when neither is
   * running, so the cost of the overhang is one rounded-box distance.
   */
  margin: 26,
  /** Taps taken along the travel vector while a card is moving. */
  blurTaps: 6,
  /**
   * Exponent of the stretched travel highlight, as a fraction of `shininess`.
   * Lower than the base lobe: a moving card smears its highlight, it does not
   * sharpen it.
   */
  /**
   * Raised from 0.42. At that share the anisotropic exponent was 17.6, a
   * 16.1-degree half-angle — again three times wider than the card's own
   * 5-degree sweep, so the travel highlight brightened the whole face uniformly
   * and read as a flash rather than a streak. The smear is supposed to come
   * from the tangent squeeze below, not from widening the lobe in *both* axes,
   * which destroys the anisotropy the term exists for.
   */
  anisoShare: 2.2,
  /**
   * How far the card's side face tips up out of the table plane.
   *
   * Lowered from 0.55, which tipped the cut face 28.8 degrees up — it shaded
   * almost like the top face and never contrasted against it. A cut card edge
   * is a near-vertical wall with a small roll: 0.2 is about 11 degrees.
   */
  edgeNormalZ: 0.32,
} as const;

/* ------------------------------------------------------------------ *
 * GLSL constant injection — colour from tokens, geometry from layout.
 * ------------------------------------------------------------------ */

function f(value: number): string {
  return Number.isInteger(value) ? `${value}.0` : `${value}`;
}

function glslRgb(hex: number): string {
  const r = ((hex >> 16) & 0xff) / 255;
  const g = ((hex >> 8) & 0xff) / 255;
  const b = (hex & 0xff) / 255;
  return `vec3(${r.toFixed(5)}, ${g.toFixed(5)}, ${b.toFixed(5)})`;
}

function mixHex(a: number, b: number, t: number): number {
  const ch = (v: number, shift: number): number => (v >> shift) & 0xff;
  const r = Math.round(ch(a, 16) + (ch(b, 16) - ch(a, 16)) * t);
  const g = Math.round(ch(a, 8) + (ch(b, 8) - ch(a, 8)) * t);
  const bl = Math.round(ch(a, 0) + (ch(b, 0) - ch(a, 0)) * t);
  return (r << 16) | (g << 8) | bl;
}

/**
 * The colour of the card's cut edge — the paper core, not the print. It is the
 * same warm stock `cardTexture` lays the faces down on, pulled a little toward
 * the felt so a sliver of it never reads as a white outline.
 */
const EDGE_STOCK = mixHex(mixHex(palette.white, palette.gold, 0.14), palette.plum, 0.16);

/**
 * Attenuation is normalised so a card sitting on the centre of the pile row
 * receives exactly `LIGHT.intensity`. Everything either side of that is a real
 * falloff, not a fudge factor.
 */
const REFERENCE_DISTANCE = Math.hypot(
  DESIGN.width / 2 - LIGHT.x,
  PILES.y - LIGHT.y,
  LIGHT.z,
);
const ATTENUATION_SCALE_SQ = CARD_SHADING.attenuationScale * CARD_SHADING.attenuationScale;
const ATTENUATION_NORM =
  (ATTENUATION_SCALE_SQ + REFERENCE_DISTANCE * REFERENCE_DISTANCE) / ATTENUATION_SCALE_SQ;

const VERTEX = /* glsl */ `#version 300 es
precision highp float;

in vec2 aPosition;
in vec2 aUV;

out vec2 vUV;
out vec4 vColor;
out vec2 vLocal;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec4 uColor;
uniform vec4 uWorldColorAlpha;

uniform float uLift;
uniform float uFlip;
uniform float uFlipSquash;
uniform vec2 uTilt;

const float PERSPECTIVE = ${f(CARD_SHADING.perspective)};

void main(void) {
    vec2 p = aPosition;

    float ca = cos(uFlip);
    float sa = sin(uFlip);

    // One depth value carries all three lies: how high the card is off the
    // table, how far through its turn it is, and how much it is leaning as it
    // travels. Everything downstream is a single honest perspective divide.
    float depth = uLift - p.x * sa + dot(p, uTilt);
    float scale = PERSPECTIVE / max(PERSPECTIVE - depth, PERSPECTIVE * 0.2);

    // uFlipSquash lets an outside animator own the edge-on narrowing (by
    // driving scale.x) without losing the depth, the trapezoid or the moving
    // highlight, which no amount of scaling can reproduce.
    //
    // What it must NOT drop is the *sign*. Past the half turn the quad is
    // mirrored in x by its own rotation, and the fragment shader compensates by
    // reading the print mirrored. Collapsing this to a bare 1.0 would keep the
    // texture mirror while losing the geometric one, and every card face would
    // come out reversed. faceSign is the same step() the fragment uses, so the
    // two can never disagree, and unlike sign(ca) it never returns 0 at exactly
    // the half turn.
    float faceSign = step(0.0, ca) * 2.0 - 1.0;
    float narrow = mix(faceSign, ca, uFlipSquash);
    vec2 local = vec2(p.x * narrow, p.y) * scale;

    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    vec2 ndc = (mvp * vec3(local, 1.0)).xy;

    // Feeding the divide back through w gives the rasteriser a genuine
    // perspective interpolation of vUV — the trapezoid's texture stays straight.
    float w = 1.0 / scale;
    gl_Position = vec4(ndc * w, 0.0, w);

    vUV = aUV;
    vLocal = p;
    vColor = uColor * uWorldColorAlpha;
}
`;

const FRAGMENT = /* glsl */ `#version 300 es
precision highp float;

in vec2 vUV;
in vec4 vColor;
in vec2 vLocal;

out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uNormalTexture;

uniform vec4 uFaceRect;
uniform vec4 uBackRect;
uniform vec4 uNormalRect;
uniform vec2 uCardPos;
uniform vec2 uCardRot;
uniform vec2 uTilt;
uniform vec3 uGlowColour;
uniform float uLift;
uniform float uFlip;
uniform float uWild;
uniform float uGloss;
uniform float uGlow;
uniform float uShade;

// xy: centre of the occluding card in this card's own frame, design units.
// zw: cos/sin of its rotation relative to this one.
uniform vec4 uOccluder;
// xy: its half extents. z: penumbra radius. w: peak darkening, 0 = none.
uniform vec4 uOccluderBox;
// xy: this frame's travel in this card's own frame, design units. z: strength.
uniform vec3 uBlur;
// xy: unit travel direction on the table. z: strength. w: how far the lobe
// stretches along that direction.
uniform vec4 uSheen;
// xy: unit direction away from the light, on the table. z: sliver width in
// design units, 0 = none. w: brightness.
uniform vec4 uEdge;

const vec3  KEY_POS       = vec3(${f(LIGHT.x)}, ${f(LIGHT.y)}, ${f(LIGHT.z)});
const vec3  KEY_COLOUR    = ${glslRgb(LIGHT.colour)};
const float KEY_INTENSITY = ${f(LIGHT.intensity)};
const vec3  FILL_POS      = vec3(${f(LIGHT.fill.x)}, ${f(LIGHT.fill.y)}, ${f(LIGHT.fill.z)});
const vec3  FILL_COLOUR   = ${glslRgb(LIGHT.fill.colour)};
const float FILL_INTENSITY= ${f(LIGHT.fill.intensity)};
const float AMBIENT       = ${f(LIGHT.ambient)};

const float ATTEN_SQ      = ${f(ATTENUATION_SCALE_SQ)};
const float ATTEN_NORM    = ${ATTENUATION_NORM.toFixed(6)};

const vec2  TABLE_CENTRE  = vec2(${f(DESIGN.width / 2)}, ${f(DESIGN.height / 2)});
const float VIEW_DIST     = ${f(CARD_SHADING.viewDistance)};
const float VIEW_PARALLAX = ${f(CARD_SHADING.viewParallax)};

const float SHININESS     = ${f(CARD_SHADING.shininess)};
const float ANISO_SHINE   = ${(CARD_SHADING.shininess * CARD_SHADING.anisoShare).toFixed(4)};
const float SPEC_BASE     = ${f(CARD_SHADING.specBase)};
const float SPEC_LIFT     = ${f(CARD_SHADING.specLift)};
const float LIFT_REF      = ${f(CARD_SHADING.liftReference)};
const float SHEEN         = ${f(CARD_SHADING.sheen)};

const vec3  WILD_TINT     = ${glslRgb(palette.violet)};
const vec3  EDGE_COLOUR   = ${glslRgb(EDGE_STOCK)};
const float EDGE_NZ       = ${f(CARD_SHADING.edgeNormalZ)};

const vec2  CARD_HALF     = vec2(${f(CARD.width / 2)}, ${f(CARD.height / 2)});
const vec2  CARD_INV      = vec2(${(1 / CARD.width).toFixed(8)}, ${(1 / CARD.height).toFixed(8)});
const float CARD_R        = ${f(CARD.cornerRadius)};

const int   BLUR_TAPS     = ${CARD_SHADING.blurTaps};
const float BLUR_NORM     = ${(1 / CARD_SHADING.blurTaps).toFixed(8)};

/** Distance from a rounded box centred on the origin. Negative inside. */
float roundedBox(vec2 p, vec2 halfSize, float radius) {
    vec2 q = abs(p) - (halfSize - vec2(radius));
    return length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0) - radius;
}

/**
 * One tap out of the card's own atlas cell, faded out across the last half
 * pixel of the cell so the silhouette stays antialiased now that the quad no
 * longer ends there, and cut to nothing beyond it so a smear can never drag a
 * neighbouring card in off the atlas.
 */
vec4 cell(vec4 rect, vec2 q, vec2 fw) {
    vec2 edge = min(q, vec2(1.0) - q);
    vec2 cov = clamp(edge / fw + 0.5, 0.0, 1.0);
    return texture(uTexture, rect.xy + clamp(q, 0.0, 1.0) * rect.zw) * (cov.x * cov.y);
}

void main(void) {
    float ca = cos(uFlip);
    float sa = sin(uFlip);

    // Which printed side faces the camera. Resolved here, from the turn angle
    // itself, so the swap always lands exactly on the half turn — no animation
    // callback can be late with it. uFlip = 0 shows the back, PI shows the face.
    float front = step(0.0, ca);
    float side = front * 2.0 - 1.0;

    // The card's own screen-aligned frame, in design units: the vertex stage
    // mirrors the quad in x past the half turn, so undoing that here gives one
    // coordinate the sliver, the crevice and the smear can all be written in.
    vec2 g = vec2(vLocal.x * side, vLocal.y);
    float sd = roundedBox(g, CARD_HALF, CARD_R);

    // Outside the print, only the thickness sliver and the motion smear can
    // paint. With neither asked for, the overhang is dead area — drop it before
    // it costs a texture read.
    float blurReach = length(uBlur.xy) * uBlur.z;
    if (sd > max(uEdge.z, blurReach) + 1.5) discard;

    // Past the half turn the quad is mirrored in x by its own rotation, which is
    // physically correct — so the print on the far side has to be read mirrored
    // to come out the right way round on screen.
    vec2 quv = vec2(mix(1.0 - vUV.x, vUV.x, front), vUV.y);
    vec4 rect = mix(uFaceRect, uBackRect, front);
    // Taken outside every branch below: the taps are a constant offset apart, so
    // they share one derivative, and derivatives inside control flow are not
    // something to rely on.
    vec2 fw = max(fwidth(quv), vec2(1e-5));

    vec4 base;                                               // premultiplied
    float smear = 0.0;
    if (uBlur.z > 0.0) {
        // The shutter opened where the card was and closed where it is, so the
        // card-space coordinate of a fixed screen point runs *forward* along the
        // travel across the exposure. Sampling that way puts the trail behind
        // the card rather than in front of it.
        vec2 stepQ = uBlur.xy * CARD_INV * uBlur.z;
        // Six taps across a smear that can be a sixth of the card leaves a
        // visible ladder on anything with an edge — a pip repeated six times
        // instead of a streak. Offsetting the ladder per fragment by an
        // interleaved-gradient hash turns that banding into noise, which is
        // both the right answer and the one the grain pass already hides.
        float dither = fract(52.9829189 *
            fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
        base = vec4(0.0);
        for (int i = 0; i < BLUR_TAPS; i++) {
            base += cell(rect, quv + stepQ * ((float(i) + dither) * BLUR_NORM), fw);
        }
        base *= BLUR_NORM;
        smear = clamp(length(stepQ) * 6.0, 0.0, 1.0);
    } else {
        base = cell(rect, quv, fw);
    }

    vec2 nuv = uNormalRect.xy + clamp(quv, 0.0, 1.0) * uNormalRect.zw;
    vec3 nmap = texture(uNormalTexture, nuv).xyz * 2.0 - 1.0;
    // A smeared print has no legible paper tooth. Flattening the normal with the
    // blur stops the lighting staying razor sharp over a blurred card.
    nmap.xy *= 1.0 - 0.75 * smear;

    // Tangent-space normal of whichever side we are actually looking at. Viewing
    // the reverse mirrors x and inverts z; the geometry mirrors with it, so the
    // bevel keeps lighting the correct screen edge right through the turn.
    vec3 n = vec3(nmap.x * side, nmap.y, nmap.z * side);
    // Turn it with the card...
    n = vec3(n.x * ca + n.z * sa, n.y, -n.x * sa + n.z * ca);
    // ...lean it with the travel tilt...
    n.xy -= uTilt;
    // ...then spin it into table space with the card's own rotation.
    vec3 N = normalize(vec3(
        n.x * uCardRot.x - n.y * uCardRot.y,
        n.x * uCardRot.y + n.y * uCardRot.x,
        n.z));

    // Exactly where this fragment sits on the table, in design units.
    vec2 rotated = vec2(
        vLocal.x * uCardRot.x - vLocal.y * uCardRot.y,
        vLocal.x * uCardRot.y + vLocal.y * uCardRot.x);
    vec3 P = vec3(uCardPos + rotated, uLift);

    vec3 toKey = KEY_POS - P;
    float keyDist = length(toKey);
    vec3 L = toKey / max(keyDist, 1.0);
    float atten = (ATTEN_SQ / (ATTEN_SQ + keyDist * keyDist)) * ATTEN_NORM;

    vec3 V = normalize(vec3((TABLE_CENTRE - P.xy) * VIEW_PARALLAX, VIEW_DIST));
    vec3 H = normalize(L + V);

    float ndl = max(dot(N, L), 0.0);
    vec3 F = normalize(FILL_POS - P);
    float ndf = max(dot(N, F), 0.0);

    vec3 shade = vec3(AMBIENT)
               + KEY_COLOUR * (KEY_INTENSITY * ndl * atten)
               + FILL_COLOUR * (FILL_INTENSITY * ndf);
    // Gentle shoulder so a raking highlight rolls off instead of clipping flat.
    shade = shade / (1.0 + 0.45 * max(shade - vec3(0.92), vec3(0.0)));

    // Contact occlusion from the card resting or hovering above this one. The
    // CPU hands over one rounded box in this card's frame, already pushed away
    // from the light by the gap between them — so the same number is a hard
    // black crevice when they touch and a soft grey pool when they do not.
    float ao = 1.0;
    if (uOccluderBox.w > 0.0) {
        vec2 d = g - uOccluder.xy;
        vec2 o = vec2(
            d.x * uOccluder.z + d.y * uOccluder.w,
           -d.x * uOccluder.w + d.y * uOccluder.z);
        float od = max(roundedBox(o, uOccluderBox.xy, CARD_R), 0.0);
        ao = 1.0 - uOccluderBox.w * exp(-od / uOccluderBox.z);
    }

    float lift01 = clamp(uLift / LIFT_REF, 0.0, 1.6);
    float gloss = uGloss * (SPEC_BASE + SPEC_LIFT * lift01);
    // The atten factor was missing here while the diffuse key and the aniso sheen both
    // had it, so a card on the right of the table got a 25% dimmer body with an
    // undimmed highlight — the highlight-to-body ratio was off by a third
    // between the two ends of the pile row.
    float spec = pow(max(dot(N, H), 0.0), SHININESS) * gloss * ndl * atten;

    // Fresnel.
    //
    // A coated card is a strong dielectric: reflectance climbs from about 0.04
    // face-on to near 1.0 at grazing. The flip sweeps through exactly that
    // range, and without this it merely got narrower and dimmer at the moment
    // it should flare. It also gives the bevel a rim for free, since the bevel
    // normal tips away from the eye.
    float ndv = max(dot(N, V), 0.0);
    float fres = 0.04 + 0.96 * pow(1.0 - ndv, 5.0);
    spec *= 1.0 + 24.0 * fres;
    // Energy the specular gains has to come from somewhere.
    shade *= mix(1.0, 0.82, fres);

    // The travel highlight: the same half vector with its component along the
    // card's motion squeezed, which stretches the lobe out along the direction
    // of travel. Card stock with a light coating, not chrome — the CPU keeps the
    // gain small and hands a wild only a fraction of it, so this never competes
    // with the foil sweep.
    if (uSheen.z > 0.0) {
        vec3 T = vec3(uSheen.xy, 0.0);
        vec3 Hs = normalize(H - T * (dot(H, T) * uSheen.w));
        spec += pow(max(dot(N, Hs), 0.0), ANISO_SHINE) * uSheen.z * ndl * atten;
    }

    // A sheen band dragged across the face by the turn. Together with the
    // trapezoid it is what stops a flip reading as a horizontal squash.
    float bandX = 0.5 - 0.5 * sa * side;
    float d = (quv.x - bandX) * 3.2;
    float sheen = exp(-d * d) * abs(sa) * SHEEN;

    vec3 lit = base.rgb * shade * (uShade * ao);
    lit += (spec + sheen) * ao * KEY_COLOUR * base.a;
    lit += WILD_TINT * (uWild * 0.07 * base.a * (0.35 + 0.65 * ndl) * ao);

    // Emissive channel for the motion layer: a rim that follows the bevel plus
    // a flat body glow, so a 21 snap or a wild reveal can light the card itself.
    // Emission is not occluded — a card glowing in a crevice still glows.
    float rim = pow(1.0 - abs(N.z), 2.2);
    lit += uGlowColour * (uGlow * (0.28 + 0.85 * rim) * base.a);

    // The card's own thickness: a sliver of cut stock standing along the side
    // facing away from the light — the same side the drop shadow is thrown to,
    // so the two agree about which way the card's far edge points. It widens
    // with lift, so a card in the air shows more of its edge than one lying flat.
    float outA = base.a;
    if (uEdge.z > 0.0) {
        vec2 q = abs(g) - (CARD_HALF - vec2(CARD_R));
        vec2 outward = max(q, vec2(0.0)) * sign(g);
        float ol = length(outward);
        vec2 nrm = ol > 1e-4 ? outward / ol : vec2(0.0);

        // The away-from-light direction, brought into the card's own frame.
        vec2 away = vec2(
            uEdge.x * uCardRot.x + uEdge.y * uCardRot.y,
           -uEdge.x * uCardRot.y + uEdge.y * uCardRot.x);
        float facing = max(dot(nrm, away), 0.0);

        float aa = max(fwidth(sd), 0.5);
        float band = clamp((uEdge.z * facing - sd) / aa, 0.0, 1.0)
                   * step(0.0, sd) * facing;

        // The cut face stands up out of the table, tipped a little by the
        // bevel, and is lit by the same key and fill as everything else.
        vec3 eN = normalize(vec3(uEdge.xy, EDGE_NZ));

        // Wrapped diffuse, not clamped Lambert.
        //
        // Which edge you see is a camera fact, so for a card left of the table
        // centre it is the LEFT edge — and the key is up and to the right, so a
        // hard clamped Lambert left that edge on ambient alone and it rendered as
        // a dead grey band on the card's lit side.
        //
        // A cut paper edge is never dead. It is a bright core that picks up
        // bounce off the card's own face and off the felt even when it faces
        // away from the key. Wrapping the term keeps it warm in shadow without
        // letting it blow out when it genuinely faces the light.
        // Only the KEY is wrapped. The fill is a cool blue bounce, and wrapping
        // it too put a floor of blue on every edge regardless of which way it
        // faced — which reads as a violet halo around the card rather than as
        // paper. The warm wrap is what makes the edge look like lit stock; the
        // cool one only tints it.
        float eKey = max((dot(eN, L) + 0.45) / 1.45, 0.0);
        float eFill = max(dot(eN, F), 0.0);
        vec3 eShade = vec3(AMBIENT)
                    + KEY_COLOUR * (KEY_INTENSITY * eKey * atten)
                    + FILL_COLOUR * (FILL_INTENSITY * eFill);
        float over = band * (1.0 - base.a);
        lit += EDGE_COLOUR * eShade * (uShade * ao * uEdge.w * over);
        outA = base.a + over;
    }

    finalColor = vec4(lit, outA) * vColor;
}
`;

/* ------------------------------------------------------------------ *
 * Geometry
 * ------------------------------------------------------------------ */

let geometry: MeshGeometry | null = null;

/**
 * The shared card quad, centred on its own origin and sized in design units.
 * Immutable and shared by every card — the vertex shader does all the warping.
 *
 * It overhangs the printed card by `CARD_SHADING.margin` on every side, and the
 * UVs are authored so `0..1` still lands exactly on the print. That is the room
 * the thickness sliver and the tail of a motion smear paint into; the fragment
 * shader discards the overhang whenever neither is running.
 */
export function getCardGeometry(): MeshGeometry {
  if (!geometry) {
    const m = CARD_SHADING.margin;
    const hw = CARD.width / 2 + m;
    const hh = CARD.height / 2 + m;
    const mu = m / CARD.width;
    const mv = m / CARD.height;
    geometry = new MeshGeometry({
      positions: new Float32Array([-hw, -hh, hw, -hh, hw, hh, -hw, hh]),
      uvs: new Float32Array([-mu, -mv, 1 + mu, -mv, 1 + mu, 1 + mv, -mu, 1 + mv]),
      indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
    });
  }
  return geometry;
}

/* ------------------------------------------------------------------ *
 * Shader
 * ------------------------------------------------------------------ */

/** Default emissive colour: the wild's violet, the loudest thing on the table. */
const DEFAULT_GLOW: readonly [number, number, number] = [
  ((palette.violetLight >> 16) & 0xff) / 255,
  ((palette.violetLight >> 8) & 0xff) / 255,
  (palette.violetLight & 0xff) / 255,
];

function makeUniforms() {
  return new UniformGroup({
    uFaceRect: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
    uBackRect: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
    uNormalRect: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
    uCardPos: { value: new Float32Array([DESIGN.width / 2, DESIGN.height / 2]), type: 'vec2<f32>' },
    uCardRot: { value: new Float32Array([1, 0]), type: 'vec2<f32>' },
    uTilt: { value: new Float32Array([0, 0]), type: 'vec2<f32>' },
    uGlowColour: { value: new Float32Array(DEFAULT_GLOW), type: 'vec3<f32>' },
    uOccluder: { value: new Float32Array([0, 0, 1, 0]), type: 'vec4<f32>' },
    uOccluderBox: { value: new Float32Array([0, 0, 1, 0]), type: 'vec4<f32>' },
    uBlur: { value: new Float32Array([0, 0, 0]), type: 'vec3<f32>' },
    uSheen: { value: new Float32Array([1, 0, 0, 0]), type: 'vec4<f32>' },
    uEdge: { value: new Float32Array([0, 1, 0, 0]), type: 'vec4<f32>' },
    uLift: { value: 0, type: 'f32' },
    uFlip: { value: 0, type: 'f32' },
    uFlipSquash: { value: 1, type: 'f32' },
    uWild: { value: 0, type: 'f32' },
    uGloss: { value: 1, type: 'f32' },
    uGlow: { value: 0, type: 'f32' },
    uShade: { value: 1, type: 'f32' },
  });
}

type CardUniformGroup = ReturnType<typeof makeUniforms>;

let program: GlProgram | null = null;

function getProgram(): GlProgram {
  program ??= GlProgram.from({ vertex: VERTEX, fragment: FRAGMENT, name: 'card-lighting' });
  return program;
}

function writeRect(target: Float32Array, uv: CardUV): void {
  target[0] = uv.x;
  target[1] = uv.y;
  target[2] = uv.w;
  target[3] = uv.h;
}

/**
 * One instance per card. The GL program is shared and compiled once; only the
 * uniform block is per-card, which is the cheap half.
 */
export class CardLightingShader extends Shader {
  readonly cardUniforms: CardUniformGroup;

  constructor() {
    const uniforms = makeUniforms();
    super({
      glProgram: getProgram(),
      resources: {
        uTexture: getFaceAtlasSource(),
        uNormalTexture: getNormalAtlasSource(),
        cardUniforms: uniforms,
      },
    });
    this.cardUniforms = uniforms;
  }

  /** UV rect of the printed face inside the face atlas. */
  setFaceRect(uv: CardUV): void {
    writeRect(this.cardUniforms.uniforms.uFaceRect, uv);
  }

  /** UV rect of the card back inside the face atlas. */
  setBackRect(uv: CardUV): void {
    writeRect(this.cardUniforms.uniforms.uBackRect, uv);
  }

  /** UV rect of the paper-tooth variant inside the normal atlas. */
  setNormalRect(uv: CardUV): void {
    writeRect(this.cardUniforms.uniforms.uNormalRect, uv);
  }

  /** Card centre in design units. Drives the whole lighting solution. */
  setCardPosition(x: number, y: number): void {
    const target = this.cardUniforms.uniforms.uCardPos;
    target[0] = x;
    target[1] = y;
  }

  /** Card rotation on the table, in radians. */
  setCardRotation(radians: number): void {
    const target = this.cardUniforms.uniforms.uCardRot;
    target[0] = Math.cos(radians);
    target[1] = Math.sin(radians);
  }

  /**
   * Surface lean, in card-local space, as a slope vector. Small values only —
   * this is the parallax between highlight and geometry that sells thickness.
   */
  setTilt(x: number, y: number): void {
    const target = this.cardUniforms.uniforms.uTilt;
    target[0] = x;
    target[1] = y;
  }

  /** Virtual height above the table, in design units. */
  setLift(value: number): void {
    this.cardUniforms.uniforms.uLift = value;
  }

  /**
   * The card resting or hovering above this one, as a rounded box in this
   * card's own frame.
   *
   * `x`/`y` is its centre and `cos`/`sin` its rotation relative to this card,
   * both in design units and radians; `halfW`/`halfH` are its half extents;
   * `penumbra` is the distance the darkening falls off over and `strength` its
   * peak. Pass `strength = 0`, or call `clearOcclusion()`, to switch the whole
   * term off — the fragment shader branches on it.
   */
  setOcclusion(
    x: number,
    y: number,
    cos: number,
    sin: number,
    halfW: number,
    halfH: number,
    penumbra: number,
    strength: number,
  ): void {
    const u = this.cardUniforms.uniforms;
    const box = u.uOccluderBox;
    if (!(strength > 0)) {
      box[3] = 0;
      return;
    }
    const occluder = u.uOccluder;
    occluder[0] = x;
    occluder[1] = y;
    occluder[2] = cos;
    occluder[3] = sin;
    box[0] = halfW;
    box[1] = halfH;
    box[2] = penumbra > 0.25 ? penumbra : 0.25;
    box[3] = strength;
  }

  /** Drop the inter-card occlusion. */
  clearOcclusion(): void {
    this.cardUniforms.uniforms.uOccluderBox[3] = 0;
  }

  /**
   * This frame's travel, in the card's own frame and in design units, plus a
   * strength. Zero strength is the single-tap path — a resting card is exactly
   * the image it was before motion blur existed.
   */
  setMotionBlur(x: number, y: number, strength: number): void {
    const target = this.cardUniforms.uniforms.uBlur;
    if (!(strength > 0)) {
      target[2] = 0;
      return;
    }
    target[0] = x;
    target[1] = y;
    target[2] = strength;
  }

  /**
   * The travel highlight: a unit direction on the table for the lobe to stretch
   * along, a `gain` (0 disables it) and an `anisotropy` of 0..1 for how far it
   * stretches.
   */
  setSheen(dirX: number, dirY: number, gain: number, anisotropy: number): void {
    const target = this.cardUniforms.uniforms.uSheen;
    if (!(gain > 0)) {
      target[2] = 0;
      return;
    }
    target[0] = dirX;
    target[1] = dirY;
    target[2] = gain;
    target[3] = anisotropy;
  }

  /**
   * The card's cut edge: a unit direction on the table pointing away from the
   * light, the sliver's width in design units (0 disables it) and a brightness.
   */
  setEdge(dirX: number, dirY: number, width: number, gain: number): void {
    const target = this.cardUniforms.uniforms.uEdge;
    if (!(width > 0) || !(gain > 0)) {
      target[2] = 0;
      return;
    }
    target[0] = dirX;
    target[1] = dirY;
    target[2] = width;
    target[3] = gain;
  }

  /**
   * Turn angle in radians. `0` shows the card back; `PI` shows the printed
   * face. The swap, and the mirroring that goes with it, happen in the shader.
   */
  setFlipAngle(radians: number): void {
    this.cardUniforms.uniforms.uFlip = radians;
  }

  /**
   * Whether the shader narrows the quad as the card turns edge-on. Set to false
   * when an outside animator drives `scale.x` for the squash — the depth, the
   * trapezoid and the travelling highlight all survive either way.
   */
  setFlipSquash(enabled: boolean): void {
    this.cardUniforms.uniforms.uFlipSquash = enabled ? 1 : 0;
  }

  /** Adds a violet bias under the foil for J♠ / J♣. */
  setWild(amount: number): void {
    this.cardUniforms.uniforms.uWild = amount;
  }

  /** Specular multiplier. 1 is the printed stock. */
  setGloss(value: number): void {
    this.cardUniforms.uniforms.uGloss = value;
  }

  /** Emissive strength, 0..1. The motion layer's `glow` channel. */
  setGlow(value: number): void {
    this.cardUniforms.uniforms.uGlow = value;
  }

  /** Emissive colour as a packed hex, e.g. `palette.mint` for a 21. */
  setGlowColour(hex: number): void {
    const target = this.cardUniforms.uniforms.uGlowColour;
    target[0] = ((hex >> 16) & 0xff) / 255;
    target[1] = ((hex >> 8) & 0xff) / 255;
    target[2] = (hex & 0xff) / 255;
  }

  /** Overall dimming, for cards buried under others in a pile. */
  setShade(value: number): void {
    this.cardUniforms.uniforms.uShade = value;
  }

  /** Restore every uniform to the state a brand-new card would have. */
  resetUniforms(): void {
    const u = this.cardUniforms.uniforms;
    u.uCardPos[0] = DESIGN.width / 2;
    u.uCardPos[1] = DESIGN.height / 2;
    u.uCardRot[0] = 1;
    u.uCardRot[1] = 0;
    u.uTilt[0] = 0;
    u.uTilt[1] = 0;
    u.uGlowColour[0] = DEFAULT_GLOW[0];
    u.uGlowColour[1] = DEFAULT_GLOW[1];
    u.uGlowColour[2] = DEFAULT_GLOW[2];
    u.uOccluder[0] = 0;
    u.uOccluder[1] = 0;
    u.uOccluder[2] = 1;
    u.uOccluder[3] = 0;
    u.uOccluderBox[0] = 0;
    u.uOccluderBox[1] = 0;
    u.uOccluderBox[2] = 1;
    u.uOccluderBox[3] = 0;
    u.uBlur[0] = 0;
    u.uBlur[1] = 0;
    u.uBlur[2] = 0;
    u.uSheen[0] = 1;
    u.uSheen[1] = 0;
    u.uSheen[2] = 0;
    u.uSheen[3] = 0;
    u.uEdge[0] = 0;
    u.uEdge[1] = 1;
    u.uEdge[2] = 0;
    u.uEdge[3] = 0;
    u.uLift = 0;
    u.uFlip = 0;
    u.uFlipSquash = 1;
    u.uWild = 0;
    u.uGloss = 1;
    u.uGlow = 0;
    u.uShade = 1;
  }
}
